"use client";

import { Table } from "@tanstack/react-table";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  MoreHorizontal,
} from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import useTranslation from "@/hooks/use-translation";
import { generateUniqueKey } from "@/lib/utils";

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  maxPageButtons?: number;
}

export function DataTablePagination<TData>({
  table,
  maxPageButtons = 5,
}: DataTablePaginationProps<TData>) {
  const { t } = useTranslation("");
  const [isMobile, setIsMobile] = useState(false);
  const currentPage = table.getState().pagination.pageIndex + 1;
  const totalPages = table.getPageCount();
  const pageNumbers = [];

  // Responsive detection
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Responsive max page buttons: fewer on mobile, more on desktop
  const responsiveMaxButtons = isMobile ? 3 : maxPageButtons;

  // Calculate the range of page numbers to show
  let startPage = Math.max(1, currentPage - Math.floor(responsiveMaxButtons / 2));
  let endPage = startPage + responsiveMaxButtons - 1;

  if (endPage > totalPages) {
    endPage = totalPages;
    startPage = Math.max(1, endPage - responsiveMaxButtons + 1);
  }

  // Generate the array of page numbers to display
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  return (
    <div className="flex flex-col items-center justify-between gap-2 px-2 sm:flex-row sm:gap-4">
      {/* Total rows - Hidden on mobile to save space */}
      <div className="hidden sm:block text-muted-foreground text-xs sm:text-sm">
        {t("common.pagination.totalRows", {
          count: table.getFilteredRowModel().rows.length,
        })}
      </div>

      <div className="flex items-center gap-1">
        {/* First Page Button - Hidden on mobile */}
        <Button
          variant="ghost"
          size="icon"
          className="hidden sm:flex size-8 p-0"
          onClick={() => table.setPageIndex(0)}
          disabled={!table.getCanPreviousPage()}
        >
          <ChevronsLeft className="size-4" />
          <span className="sr-only">{t("common.pagination.firstPage")}</span>
        </Button>

        {/* Previous Page Button */}
        <Button
          variant="ghost"
          size="icon"
          className="size-7 sm:size-8 p-0"
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
        >
          <ChevronLeft className="size-3.5 sm:size-4" />
          <span className="sr-only">{t("common.pagination.previous")}</span>
        </Button>

        {/* Page Numbers */}
        <div className="flex items-center gap-0.5 sm:gap-1">
          {/* Show first page and ellipsis on mobile only if not in current range */}
          {startPage > 1 && (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="size-7 sm:size-8 p-0 text-xs sm:text-sm"
                onClick={() => table.setPageIndex(0)}
              >
                1
              </Button>
              {startPage > 2 && (
                <span className="flex size-7 sm:size-8 items-center justify-center">
                  <MoreHorizontal className="size-3 sm:size-4" />
                </span>
              )}
            </>
          )}

          {pageNumbers.map((page) => (
            <Button
              key={generateUniqueKey(page)}
              variant={page === currentPage ? "default" : "ghost"}
              size="sm"
              className={`size-7 sm:size-8 p-0 text-xs sm:text-sm ${page === currentPage ? "font-bold" : ""}`}
              onClick={() => table.setPageIndex(page - 1)}
            >
              {page}
            </Button>
          ))}

          {/* Show last page and ellipsis on mobile only if not in current range */}
          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && (
                <span className="flex size-7 sm:size-8 items-center justify-center">
                  <MoreHorizontal className="size-3 sm:size-4" />
                </span>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="size-7 sm:size-8 p-0 text-xs sm:text-sm"
                onClick={() => table.setPageIndex(totalPages - 1)}
              >
                {totalPages}
              </Button>
            </>
          )}
        </div>

        {/* Next Page Button */}
        <Button
          variant="ghost"
          size="icon"
          className="size-7 sm:size-8 p-0"
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
        >
          <ChevronRight className="size-3.5 sm:size-4" />
          <span className="sr-only">{t("common.pagination.next")}</span>
        </Button>

        {/* Last Page Button - Hidden on mobile */}
        <Button
          variant="ghost"
          size="icon"
          className="hidden sm:flex size-8 p-0"
          onClick={() => table.setPageIndex(table.getPageCount() - 1)}
          disabled={!table.getCanNextPage()}
        >
          <ChevronsRight className="size-4" />
          <span className="sr-only">{t("common.pagination.lastPage")}</span>
        </Button>
      </div>

    </div>
  );
}
